import { Component, OnDestroy, Renderer2, ViewChild } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter, Subject, Subscription, takeUntil } from "rxjs";
import { AppSidebarComponent } from "src/app/layout/app.sidebar.component";
import { AppTopBarComponent } from "src/app/layout/app.topbar.component";
import { LayoutService } from "src/app/layout/service/app.layout.service";

@Component({
  templateUrl: "./app.layout.component.html",
  styleUrls: ["./app.layout.component.scss"],
})
export class AppLayoutComponent implements OnDestroy {
  private readonly destroySubject = new Subject<void>();
  overlayMenuOpenSubscription: Subscription;
  menuOutsideClickListener: (() => void) | null = null;
  profileMenuOutsideClickListener: (() => void) | null = null;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopBarComponent) appTopbar!: AppTopBarComponent;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
  ) {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen("document", "click", (event) => {
            const isOutsideClicked = !(
              this.appSidebar.el.nativeElement.isSameNode(event.target) // eslint-disable-line @typescript-eslint/no-unsafe-call
              || this.appSidebar.el.nativeElement.contains(event.target) // eslint-disable-line @typescript-eslint/no-unsafe-call
              || this.appTopbar.menuButton.nativeElement.isSameNode(event.target)
              || this.appTopbar.menuButton.nativeElement.contains(event.target)
            );
            if (isOutsideClicked) {
              this.hideMenu();
            }
          });
        }

        if (!this.profileMenuOutsideClickListener) {
          this.profileMenuOutsideClickListener = this.renderer.listen("document", "click", (event) => {
            const isOutsideClicked = !(
              this.appTopbar.menu.nativeElement.isSameNode(event.target) // eslint-disable-line @typescript-eslint/no-unsafe-call
              || this.appTopbar.menu.nativeElement.contains(event.target) // eslint-disable-line @typescript-eslint/no-unsafe-call
              || this.appTopbar.topbarMenuButton.nativeElement.isSameNode(event.target)
              || this.appTopbar.topbarMenuButton.nativeElement.contains(event.target)
            );

            if (isOutsideClicked) {
              this.hideProfileMenu();
            }
          });
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.hideMenu();
        this.hideProfileMenu();
      });
  }

  hideMenu(): void {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  hideProfileMenu(): void {
    this.layoutService.state.profileSidebarVisible = false;
    if (this.profileMenuOutsideClickListener) {
      this.profileMenuOutsideClickListener();
      this.profileMenuOutsideClickListener = null;
    }
  }

  blockBodyScroll(): void {
    document.body.classList.add("blocked-scroll");
  }

  unblockBodyScroll(): void {
    document.body.classList.remove("blocked-scroll");
  }

  get containerClass(): Record<string, boolean> {
    return {
      "layout-theme-light": this.layoutService.config.colorScheme === "light",
      "layout-theme-dark": this.layoutService.config.colorScheme === "dark",
      "layout-overlay": this.layoutService.config.menuMode === "overlay",
      "layout-static": this.layoutService.config.menuMode === "static",
      "layout-static-inactive":
        this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config.menuMode === "static",
      "layout-overlay-active": this.layoutService.state.overlayMenuActive,
      "layout-mobile-active": this.layoutService.state.staticMenuMobileActive,
      "p-input-filled": this.layoutService.config.inputStyle === "filled",
      "p-ripple-disabled": !this.layoutService.config.ripple,
    };
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
